/* eslint-disable react/boolean-prop-naming */
"use client";
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import RadioButton from "../../atoms/radioButton";
import { CALENDAR, COUNTRIES } from "../../../constants/common/common";
import TimeSlots from "../timeSlots/timeSlots";
import {
  requestToFetchInternationalDeliveryDates,
  requestToFetchShippingAndTimeSlotAction,
  requestToFetchInternationalShippingAndTimeSlot,
} from "../../../store/slices/common/shippingDateAndSlotSelection";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../components/atoms/loader";
import isEmpty from "lodash.isempty";
import { getProductDeliveryType, processDeliveryDate, updateCurrency } from "../../../utils/common";
import { plpPageConstants } from "../../../constants/pageConstants/plpConstants";
import { setGaShippingMethodName, setGaTimeSlotSelected } from "../../../analytics/gaEvents";
import useAnalytics from "../../../analytics/useAnalytics";
import { cleverTapCommonEvents, cleverTapCommonWords } from "../../../analytics/clevertapConstants";
import { GA4_EVENTS } from "../../../analytics/gaConstant";
import { calenderIsTodayOrTomorrowDate, getShippingMethodsInStringAndCount } from "../../../utils/common/deliveryDate";

/**
 * Represents the shipping methods component for selecting a time slot and shipping method.
 *
 * This component allows users to select a shipping method and a corresponding time slot.
 * It handles both domestic and international shipping scenarios, with optional time slot selection.
 *
 * @component
 * @param {Object} props - Component props.
  @param {Function} props.onTimeAndShippingMethodSelection - Callback function to handle the selection of time slot and shipping method.
 * @param {Date} props.selectedDate - The currently selected date for the shipping method.
 * @param {Object} props.currentItem - The current item being shipped, including details for shipping.
 * @param {Function} props.setCurrentComponent - Function to set the current component. Default is a no-op function.
 * @param {boolean} props.showTimeSlots - Flag to indicate whether time slots should be displayed for shipping.
 * @param {Object} props.currentShippingMethod - The current selected shipping method, with method details.
 * @param {Function} props.setCurrentShippingMethod - Function to update the selected shipping method.
 * @param {boolean} [props.isInternational=false] - Flag to indicate whether the shipping method is for international shipping. Default is `false`.
 * @param {String} [props.selectedCurrency] -By default selected currency
 * @returns {React.ReactElement} A React element displaying the available shipping methods and time slots, or a fallback UI if no shipping methods are available.
 */
const ShippingMethods = ({
  onTimeAndShippingMethodSelection,
  selectedDate,
  currentItem,
  setCurrentComponent = () => {},
  showTimeSlots,
  currentShippingMethod,
  setCurrentShippingMethod,
  isInternational = false,
  selectedCurrency,
  popupCleverTapData,
  cartCleverTapData,
  isCart,
  setIsSameShippingSelected,
  setUserSelectedTimeSlotId,
}) => {
  const dispatch = useDispatch();
  const updatedCleverTapData = useRef(null);
  const { trackEvent } = useAnalytics();
  const { selectedIntLocation, selectedDomesticLocation } = useSelector((state) => state?.locationLock);
  const { shippingAndTimeSlot, internationalDeliveryMethods, internationalTimeSlotsByDate, loading } = useSelector(
    (state) => state.shippingDateAndSlotSelection,
  );

  const pinCode = selectedDomesticLocation?.pincode;
  const address = selectedDomesticLocation?.address;

  const selectedInternationalMethod = showTimeSlots
    ? internationalTimeSlotsByDate?.shippingDetails
    : internationalDeliveryMethods?.shippingDetails;
  const domesticShippingMethods = shippingAndTimeSlot?.data;

  const shippingDetails = isInternational ? selectedInternationalMethod : domesticShippingMethods;

  useEffect(() => {
    if (!isEmpty(pinCode) && !isInternational) {
      const queryParams = {
        productId: currentItem?.productId,
        pinCode: isCart ? currentItem?.pincode : pinCode,
        deliveryDate: selectedDate.replace(/-/g, "/"),
        countryGeoId: "IND",
      };
      dispatch(requestToFetchShippingAndTimeSlotAction(queryParams));
    } else if (isInternational && selectedDate?.length > 0) {
      const queryParams = {
        productId: currentItem?.productId,
        pinCode: isCart ? currentItem?.pincode : selectedIntLocation?.pincode,
        deliveryDate: selectedDate.replace(/-/g, "/"),
        countryGeoId: currentItem?.catalogId || currentItem?.productCatalogId,
      };
      dispatch(requestToFetchInternationalShippingAndTimeSlot(queryParams));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinCode, isInternational, currentItem, selectedDate]);

  useEffect(() => {
    if (shippingDetails) {
      const { methodsString, methodsCount } = getShippingMethodsInStringAndCount(shippingDetails);

      const isInternationalBasedOnClickedItem =
        (currentItem?.catalogId || currentItem?.productCatalogId) !== COUNTRIES.INDIA;
      const productDeliveryType = getProductDeliveryType(currentItem?.primaryCategoryId);
      const urlPath = currentItem?.urlIdentifier;

      const cleverTapDataShippingViewed = {
        page_name: urlPath ? urlPath : cleverTapCommonWords.CART,
        page_type: urlPath ? cleverTapCommonWords.PDP : cleverTapCommonWords.CART,
        product_name: currentItem?.productName,
        pid: currentItem?.productId,
        product_category: currentItem?.primaryCategoryId || currentItem?.primaryProductCategoryId,
        product_price: currentItem?.productPrice?.price || currentItem?.mainProduct?.price,
        delivery_type: productDeliveryType,
        pincode: isInternationalBasedOnClickedItem ? "" : pinCode,
        area: isInternationalBasedOnClickedItem ? "" : address,
        system_pincode: isInternationalBasedOnClickedItem ? "" : pinCode,
        country: currentItem?.catalogId || currentItem?.productCatalogId,
        city: isInternationalBasedOnClickedItem ? selectedIntLocation?.location : "",
        is_international: isInternationalBasedOnClickedItem,
        available_delivery_shipping_methods: methodsString,
        available_delivery_shipping_count: methodsCount,
        date_of_delivery: processDeliveryDate(selectedDate || ""),
        is_date_today: isInternationalBasedOnClickedItem
          ? ""
          : calenderIsTodayOrTomorrowDate(selectedDate)?.isDateToday,
        is_date_tomorrow: isInternationalBasedOnClickedItem
          ? ""
          : calenderIsTodayOrTomorrowDate(selectedDate)?.isDateTomorrow,
      };

      trackEvent(cleverTapCommonEvents.deliveryShippingViewed, cleverTapDataShippingViewed, ["clevertap"]);
    }
  }, [shippingDetails]);

  /**
   * Handles click on shipping method selection.
   *
   * @param {string} shippingMethodId - The ID of the selected shipping method.
   * @param {string} shippingMethodName - The name of the selected shipping method.
   * @param {string} [timeSlotId] - The ID of the selected time slot (optional).
   */
  const onHandleShippingSelection = (shippingMethodId, shippingMethodName, timeSlotId, priceDetails) => {
    trackEvent(GA4_EVENTS?.SHIPPING_METHOD_SELECTED, { ...setGaShippingMethodName(shippingMethodName) }, ["ga4"]);
    setCurrentShippingMethod({ shippingMethodId, shippingMethodName });
    if (isInternational) {
      dispatch(
        requestToFetchInternationalDeliveryDates({
          productId: currentItem?.productId,
          city: isCart ? currentItem?.pincode : selectedIntLocation?.pincode,
          shipmentType: shippingMethodId,
          timeSlotId,
        }),
      );
      setCurrentComponent(CALENDAR);
    }
    if (isCart) {
      setIsSameShippingSelected(shippingMethodId === currentItem?.shippingDetails?.shippingMethodId);
      updatedCleverTapData.current = {
        ...cartCleverTapData,
        delivery_shipping: shippingMethodName || "",
        delivery_slot: "",
        date_of_delivery: processDeliveryDate(selectedDate),
        delivery_shipping_fee: isInternational
          ? updateCurrency(selectedCurrency, priceDetails)
          : updateCurrency(selectedCurrency, priceDetails?.price),
      };
    } else {
      updatedCleverTapData.current = {
        ...popupCleverTapData,
        delivery_shipping: shippingMethodName || "",
        delivery_slot: "",
        date_of_delivery: processDeliveryDate(selectedDate),
        delivery_shipping_fee: isInternational
          ? updateCurrency(selectedCurrency, priceDetails)
          : updateCurrency(selectedCurrency, priceDetails?.price),
      };
    }
    trackEvent(cleverTapCommonEvents.deliveryShippingSelected, updatedCleverTapData.current, ["clevertap"]);
  };

  /**
   * Handles time slot selection and dispatches necessary actions.
   *
   * @param {Object} timeSlots - The selected time slot object.
   */
  const onHandleTimeSlotSelection = (e, timeSlots) => {
    e.stopPropagation();
    trackEvent(
      GA4_EVENTS?.TIME_SLOT_SELECTED,
      { ...setGaTimeSlotSelected(timeSlots?.timeSlots?.startTime, timeSlots?.timeSlots?.endTime) },
      ["ga4"],
    );
    onTimeAndShippingMethodSelection({ currentShippingMethod, timeSlots });
  };

  /**
   * Parses a delivery string with HTML tags and returns plain text.
   *
   * @param {string} deliveryString - The delivery string containing HTML.
   * @returns {string} Parsed plain text.
   */
  const parseEarliestDelivery = (deliveryString) => {
    const tempElement = document.createElement("div");
    tempElement.innerHTML = deliveryString;
    return tempElement.innerText;
  };

  /**
   * Renders a shipping method card.
   *
   * @param {Object} methodDetails - Details of the shipping method.
   * @param {string} method - The method ID.
   * @returns {React.ReactElement} The rendered card component.
   */
  const renderShippingMethodCard = (method, methodDetails) => {
    const isSelectedShippingMethod = currentShippingMethod?.shippingMethodId === method;

    return (
      <div
        className={`cursor-pointer overflow-y-auto rounded-xl border-1 border-gray-200 px-12 py-12 duration-300 ease-in-out hover:border-radioUncheck`}
        key={method}
        onClick={() =>
          isInternational && showTimeSlots
            ? {}
            : onHandleShippingSelection(
                method,
                methodDetails?.shippingMethodName,
                methodDetails?.timeSlots?.[0]?.timeSlotId,
                methodDetails?.priceDetails,
              )
        }
      >
        <div className="flex w-full items-start justify-between">
          <div className="flex items-center gap-10">
            <RadioButton
              id={methodDetails?.productShipmentMethodId}
              label=""
              value={currentShippingMethod?.shippingMethodId}
              name="shippingMethod"
              isChecked={isSelectedShippingMethod}
              radioDimension="w-20 h-20"
            />
            <div>
              <span className="text-14 font-600 text-black-100">{methodDetails?.shippingMethodName}</span>
              <br />
              <span className="text-12 font-400 text-grey-500">
                {isInternational
                  ? `${plpPageConstants.EARLIEST_DELIVERY} ${parseEarliestDelivery(methodDetails?.earliestDelivery)}`
                  : methodDetails?.longDescription}
              </span>
            </div>
          </div>
          <span className="w-[20%] text-right text-14 font-semibold text-black-100">
            {isInternational
              ? updateCurrency(selectedCurrency, methodDetails?.priceDetails)
              : updateCurrency(selectedCurrency, methodDetails?.priceDetails?.price)}
          </span>
        </div>
        {isSelectedShippingMethod && showTimeSlots && (
          <TimeSlots
            timeSlots={methodDetails?.timeSlots}
            onTimeSlotSelection={onHandleTimeSlotSelection}
            popupCleverTapData={updatedCleverTapData.current}
            cartCleverTapData={updatedCleverTapData.current}
            setUserSelectedTimeSlotId={setUserSelectedTimeSlotId}
            currentItem={currentItem}
            isCart={isCart}
            selectedCurrency={selectedCurrency}
            price={isInternational ? methodDetails?.priceDetails : methodDetails?.priceDetails?.price}
            shippingMethodName={methodDetails?.shippingMethodName}
            selectedDate={selectedDate}
          />
        )}
      </div>
    );
  };

  /**
   * Returns the appropriate shipping method card based on the international flag and time slots visibility.
   *
   * @param {boolean} isInternational - Flag to check if the shipping method is international.
   * @param {boolean} showTimeSlots - Flag to check if time slots should be displayed.
   * @param {Object} currentShippingMethod - The currently selected shipping method.
   * @param {Object} shippingDetails - The details of the available shipping methods.
   * @returns {React.ReactNode} The rendered shipping method card(s).
   */
  const getShippingMethodCards = () => {
    if (!shippingDetails) return null;
    if (isInternational) {
      if (showTimeSlots) {
        return renderShippingMethodCard(
          currentShippingMethod?.shippingMethodId,
          shippingDetails[currentShippingMethod?.shippingMethodId],
        );
      } else {
        return Object.entries(shippingDetails).map(([method, methodDetails]) =>
          renderShippingMethodCard(method, methodDetails),
        );
      }
    } else {
      return shippingDetails?.map((shipping) => {
        const shippingMethodId = Object.keys(shipping)[0];
        return renderShippingMethodCard(shippingMethodId, shipping[shippingMethodId]);
      });
    }
  };

  if (loading) return <Loader />;

  return <div className="flex flex-col gap-14">{getShippingMethodCards()}</div>;
};

ShippingMethods.propTypes = {
  onTimeAndShippingMethodSelection: PropTypes.func.isRequired,
  selectedDate: PropTypes.string,
  selectedCurrency: PropTypes.object,
  currentItem: PropTypes.object.isRequired,
  setCurrentComponent: PropTypes.func,
  showTimeSlots: PropTypes.bool,
  isInternational: PropTypes.bool,
  currentShippingMethod: PropTypes.object,
  setCurrentShippingMethod: PropTypes.func,
  popupCleverTapData: PropTypes.object,
  cartCleverTapData: PropTypes.object,
  isCart: PropTypes.bool,
  setIsSameShippingSelected: PropTypes.func,
  setUserSelectedTimeSlotId: PropTypes.func,
};

export default ShippingMethods;
